/**
 * Admin Dashboard Overview Page
 * Displays key metrics: Revenue, Transactions, Users, Games
 * Real-time updates via tRPC APIs with Recharts visualizations
 */

import { useState } from 'react';
import { TrendingUp, DollarSign, ShoppingCart, Users, RefreshCw, Loader2 } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

export default function AdminDashboard() {
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  // Fetch dashboard data using tRPC
  const metricsQuery = trpc.admin.dashboardMetrics.getMetrics.useQuery({});
  const topGamesQuery = trpc.admin.dashboardMetrics.getTopGames.useQuery({ limit: 5 });
  const recentTxQuery = trpc.admin.dashboardMetrics.getRecentTransactions.useQuery({ limit: 10 });
  const trendQuery = trpc.admin.dashboardMetrics.getTransactionTrend.useQuery({ days: 7 });
  const revenueQuery = trpc.admin.dashboardMetrics.getRevenueByPaymentMethod.useQuery({ dateRange: 'month' });

  const isLoading = metricsQuery.isLoading || topGamesQuery.isLoading || recentTxQuery.isLoading || trendQuery.isLoading || revenueQuery.isLoading;

  const handleRefresh = () => {
    void metricsQuery.refetch();
    void topGamesQuery.refetch();
    void recentTxQuery.refetch();
    void trendQuery.refetch();
    void revenueQuery.refetch();
    setLastUpdated(new Date());
  };

  const metrics = metricsQuery.data;
  const topGames = topGamesQuery.data || [];
  const recentTransactions = recentTxQuery.data || [];
  const transactionTrend = trendQuery.data || [];
  const revenueByPayment = revenueQuery.data || [];

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="animate-spin" size={40} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-black">Dashboard</h1>
            <p className="text-black text-sm mt-1">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
          </div>
          <Button onClick={handleRefresh} variant="outline" size="sm" className="backoffice-button-outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="backoffice-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 bg-black text-white rounded-t-lg">
              <CardTitle className="text-sm font-medium text-white">Total Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-white" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-black">฿{metrics?.revenue?.toLocaleString() || '0'}</div>
              <p className="text-xs text-black/70">0% from last month</p>
            </CardContent>
          </Card>

          <Card className="backoffice-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 bg-black text-white rounded-t-lg">
              <CardTitle className="text-sm font-medium text-white">Total Transactions</CardTitle>
              <ShoppingCart className="h-4 w-4 text-white" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-black">{metrics?.transactionCount?.toLocaleString() || '0'}</div>
              <p className="text-xs text-black/70">0% from last month</p>
            </CardContent>
          </Card>

          <Card className="backoffice-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 bg-black text-white rounded-t-lg">
              <CardTitle className="text-sm font-medium text-white">Active Users</CardTitle>
              <Users className="h-4 w-4 text-white" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-black">{metrics?.activeUsers?.toLocaleString() || '0'}</div>
              <p className="text-xs text-black/70">0% from last month</p>
            </CardContent>
          </Card>

          <Card className="backoffice-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 bg-black text-white rounded-t-lg">
              <CardTitle className="text-sm font-medium text-white">Success Rate</CardTitle>
              <TrendingUp className="h-4 w-4 text-white" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-black">{metrics?.successRate?.toFixed(1) || '0'}%</div>
              <p className="text-xs text-black/70">0% from last month</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Transaction Trend */}
          <Card className="backoffice-card">
            <CardHeader className="backoffice-card-header">
              <CardTitle className="text-black">Transaction Trend (7 Days)</CardTitle>
              <CardDescription className="text-black/70">Daily transaction count</CardDescription>
            </CardHeader>
            <CardContent>
              {transactionTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={transactionTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="count" stroke="#3b82f6" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Revenue by Payment Method */}
          <Card className="backoffice-card">
            <CardHeader className="backoffice-card-header">
              <CardTitle className="text-black">Revenue by Payment Method</CardTitle>
              <CardDescription className="text-black/70">Monthly breakdown</CardDescription>
            </CardHeader>
            <CardContent>
              {revenueByPayment.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={revenueByPayment}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ฿${value}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {revenueByPayment.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Transactions */}
        <Card className="backoffice-card">
          <CardHeader className="backoffice-card-header">
            <CardTitle className="text-black">Recent Transactions</CardTitle>
            <CardDescription className="text-black/70">Latest 10 transactions</CardDescription>
          </CardHeader>
          <CardContent>
            {recentTransactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-black">
                      <th className="text-left py-2 px-4 font-medium text-white">Game</th>
                      <th className="text-left py-2 px-4 font-medium text-white">Date</th>
                      <th className="text-left py-2 px-4 font-medium text-white">Amount</th>
                      <th className="text-left py-2 px-4 font-medium text-white">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentTransactions.map((tx: any, i: number) => (
                      <tr key={i} className="border-b hover:bg-gray-100">
                        <td className="py-2 px-4 text-black">{tx.gameName || 'N/A'}</td>
                        <td className="py-2 px-4 text-black">{new Date(tx.createdAt).toLocaleString()}</td>
                        <td className="py-2 px-4 text-black">฿{tx.amount?.toLocaleString() || '0'}</td>
                        <td className="py-2 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            tx.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {tx.status || 'pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground">No transactions yet</div>
            )}
          </CardContent>
        </Card>

        {/* Top Games */}
        <Card className="backoffice-card">
          <CardHeader className="backoffice-card-header">
            <CardTitle className="text-black">Top Games</CardTitle>
            <CardDescription className="text-black/70">Most popular games by transactions</CardDescription>
          </CardHeader>
          <CardContent>
            {topGames.length > 0 ? (
              <div className="space-y-4">                {topGames.map((game: any, i: number) => (
                  <div key={i} className="flex items-center justify-between pb-4 border-b last:border-0 hover:bg-gray-50 px-2 py-1 rounded">
                    <div>
                      <p className="font-medium text-black">{game.name}</p>
                      <p className="text-sm text-black/60">{game.transactionCount || 0} transactions</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-black">฿{game.revenue?.toLocaleString() || '0'}</p>
                      <p className="text-sm text-black/60">{game.successRate?.toFixed(1) || '0'}% success</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground">No game data available</div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
